import { CURRENCY_CODE, CURRENCY_PRECISION } from './order-currency';
import {
  REWARD_LINE_SUFFIX,
  SaveOrderAcknowledged,
} from './save-order-payload';
import { AppUiFieldValues } from '@omni/interfaces/app-ui/record.interface';
import {
  PricedCell,
  PricedLine,
  PricedOrder,
  currencyPrecisionOf,
  percentOff,
  roundMoney,
} from './pricing/pricing.model';
import {
  PricingItem,
  PricingPriceList,
} from '@omni/interfaces/pricing/pricing-bundle.interface';
import { OrderDraftSnapshot } from './order-draft';
import { localDayOf } from '@omni/utility/iso-day.util';
import type { ServerOrderRows } from '@omni/data-services/order-capture/order-capture.data.service';
import {
  LOOKUP_TABLE,
  formattedText,
  isoOf,
  numberOrNull,
  text,
} from '@omni/utility/dynamics-row.util';

export interface PulledOrder {
  serverId: string;
  /** The order number, e.g. ORD-01095-P7H5D8. */
  name: string;
  accountId: string | null;
  /** The server's `modifiedon`: a later one means the order changed somewhere else. */
  modifiedOn: string;
  snapshot: OrderDraftSnapshot;
  /**
   * What the server holds, as the next save's delta baseline. Every print is a placeholder, so that
   * save resends what is on the order once and removes what is not.
   */
  acknowledged: SaveOrderAcknowledged;
  values: AppUiFieldValues;
  /** Formatted `totalamount`, for the list of drafts. */
  total: string;
  /** The order's ISO currency code, when the query read it; the symbol its total is shown with. */
  currencyCode: string | null;
  /**
   * Saved without delivery rows: its lines are shown in one delivery made up here, which is for
   * viewing only; SaveOrder has never moved existing lines onto a new delivery.
   */
  withoutDeliveryRows: boolean;
}

/**
 * A server order as the device holds a draft, so a rep can carry on from another device. Line and
 * delivery ids are the server's, which the device minted, so saving it again updates rather than
 * duplicates. Rewards are recomputed on the device; only their ids are kept. Null without a price list.
 */
export function toPulledOrder(
  rows: ServerOrderRows,
  headerAttributes: readonly string[],
): PulledOrder | null {
  const { order } = rows;
  const serverId = text(order.salesorderid);
  const priceListId = text(order._pricelevelid_value);
  if (!serverId || !priceListId) return null;

  const held = pulledDeliveries(rows.deliveries, rows.lines);
  // Saved without delivery rows (before they existed, or by another app): one drop holds the lines.
  const deliveries =
    held.length || !rows.lines.length ? held : [soleDelivery(order)];
  const { quantities, lineIds, lineNumbers } = pulledLines(
    rows.lines,
    deliveries,
  );

  const values = headerValues(order, headerAttributes);
  return {
    serverId,
    name:
      text(order.ordernumber) ||
      text(order.name).replace(/^Order Number - /, ''),
    accountId: text(order._customerid_value) || null,
    modifiedOn: isoOf(order.modifiedon),
    total: formattedText(order, 'totalamount'),
    currencyCode: text(order[CURRENCY_CODE]) || null,
    withoutDeliveryRows: !held.length && rows.lines.length > 0,
    values,
    acknowledged: {
      lines: placeholders(
        rows.lines.map((line) => text(line.salesorderdetailid)),
      ),
      deliveries: placeholders(held.map((delivery) => delivery.id)),
      attributes: Object.keys(values),
      lineNumbers,
    },
    snapshot: {
      orderId: serverId,
      lineIds,
      sentLineIds: [],
      // The day it was created on the rep's clock, as a captured order's.
      asOf: localDayOf(isoOf(order.createdon)) ?? '',
      priceListId,
      bundleVersion: null,
      deliveries,
      lines: [...quantities].map(([product, byDelivery]) => ({
        product,
        quantities: byDelivery,
      })),
      header: values,
      schedule: pulledSchedule(order),
      net: Number(order.totalamount) || 0,
      currency: text(order[CURRENCY_CODE]) || null,
      firstProduct: firstProductOf(rows.lines),
    },
  };
}

/** An order as a read-only view shows it: its own lines and figures, and a list made of its products. */
export interface ViewedOrder {
  pulled: PulledOrder;
  /** Stands in for the price list: the order's products at the prices it was saved with. */
  priceList: PricingPriceList;
  /** Dynamics' amounts per line and delivery, as the engine would have shaped them. */
  priced: PricedOrder;
  /** `statecode`: what the screen's lock rule reads, alongside the header's `statuscode`. */
  state: { value: number; label: string };
}

/**
 * Any order, of any status or rep, read back to be viewed: the figures are the ones Dynamics holds, never priced again on the device. Null without a price list.
 */
export function toViewedOrder(
  rows: ServerOrderRows,
  headerAttributes: readonly string[],
): ViewedOrder | null {
  const pulled = toPulledOrder(rows, headerAttributes);
  if (!pulled) return null;
  const { order } = rows;
  const sequenceOf = new Map(
    pulled.snapshot.deliveries.map((d) => [d.id, d.sequence]),
  );
  const primary =
    pulled.snapshot.deliveries.find((d) => d.isPrimary)?.sequence ?? 1;
  const precision = currencyPrecisionOf({
    currencyPrecision: numberOrNull(order[CURRENCY_PRECISION]),
  });
  const lines = viewedLines(
    rows.lines,
    (line) => sequenceOf.get(text(line._indskr_orderdelivery_value)) ?? primary,
    precision,
  );
  const items = lines
    .filter((line) => !line.isFreeGood)
    .map((line) => viewedItem(line, rows.lines));
  const priceList: PricingPriceList = {
    ...EMPTY_LIST,
    id: pulled.snapshot.priceListId,
    name: formattedText(order, '_pricelevelid_value'),
    currency: formattedText(order, '_transactioncurrencyid_value'),
    currencyId: text(order._transactioncurrencyid_value) || null,
    currencyCode: text(order[CURRENCY_CODE]) || null,
    currencyPrecision: precision,
    items,
  };
  return {
    pulled,
    priceList,
    priced: viewedPricing(pulled, priceList, lines),
    state: {
      value: Number(order.statecode) || 0,
      label: formattedText(order, 'statecode'),
    },
  };
}

const EMPTY_LIST: Omit<PricingPriceList, 'id' | 'name' | 'currency'> = {
  minOrderValue: null,
  minOrderQuantity: null,
  minDeliveryValue: null,
  minDeliveryQuantity: null,
  minimumValueBasis: null,
  fetchXml: null,
  priority: 0,
  isDefault: false,
  validFrom: null,
  validTo: null,
  version: 0,
  items: [],
  discountLists: [],
};

/** One priced line per product, and one per product's free goods, with each delivery's share. */
function viewedLines(
  rows: readonly Record<string, unknown>[],
  sequenceOf: (line: Record<string, unknown>) => number,
  precision: number,
): PricedLine[] {
  const byKey = new Map<string, PricedLine>();
  for (const row of rows) {
    const product = text(row._productid_value);
    if (!product) continue;
    const free = row.indskr_isfreegood === true;
    const key = free ? `${product}${REWARD_LINE_SUFFIX}` : product;
    const line = byKey.get(key) ?? emptyLine(product, free, row);
    byKey.set(key, line);
    const sequence = sequenceOf(row);
    let cell = line.perDelivery.find((c) => c.sequence === sequence);
    if (!cell) {
      cell = { sequence, quantity: 0, gross: 0, net: 0 };
      line.perDelivery.push(cell);
    }
    cell.quantity += Number(row.quantity) || 0;
    if (free) continue;
    cell.gross = roundMoney(
      cell.gross + (Number(row.baseamount) || 0),
      precision,
    );
    cell.net = roundMoney(
      cell.net + (Number(row.extendedamount) || 0),
      precision,
    );
  }
  return [...byKey.values()].map((line) => withTotals(line, precision));
}

function emptyLine(
  product: string,
  free: boolean,
  row: Record<string, unknown>,
): PricedLine {
  return {
    product,
    priceListItemId: null,
    productCategory: null,
    unitPrice: Number(row.priceperunit) || 0,
    quantity: 0,
    perDelivery: [],
    gross: 0,
    volumeDiscountPct: 0,
    manualDiscountPct: 0,
    effectiveDiscountPct: free ? 100 : 0,
    net: 0,
    freeUnits: 0,
    isFreeGood: free,
    appliedRuleIds: [],
    offers: [],
  };
}

function withTotals(line: PricedLine, precision: number): PricedLine {
  const cells: PricedCell[] = [...line.perDelivery]
    .sort((a, b) => a.sequence - b.sequence)
    .map((cell) => ({
      ...cell,
      discountPct: percentOff(cell.gross, cell.net),
    }));
  const quantity = cells.reduce((sum, c) => sum + c.quantity, 0);
  const gross = roundMoney(
    cells.reduce((sum, c) => sum + c.gross, 0),
    precision,
  );
  const net = roundMoney(
    cells.reduce((sum, c) => sum + c.net, 0),
    precision,
  );
  return {
    ...line,
    perDelivery: cells,
    quantity,
    gross,
    net,
    effectiveDiscountPct: line.isFreeGood ? 100 : percentOff(gross, net),
    freeUnits: line.isFreeGood ? quantity : 0,
  };
}

function viewedItem(
  line: PricedLine,
  rows: readonly Record<string, unknown>[],
): PricingItem {
  const row = rows.find((r) => text(r._productid_value) === line.product);
  return {
    id: line.product,
    product: line.product,
    productName: formattedText(row, '_productid_value') || null,
    productCode: text(row?.['product.productnumber']) || null,
    unitPrice: line.unitPrice,
    uom: null,
    validFrom: null,
    validTo: null,
    minQuantity: null,
    maxQuantity: null,
    productCategory: null,
    incrementQuantity: null,
    quotaPerAccount: null,
    isRecommended: null,
    version: 0,
  };
}

function viewedPricing(
  pulled: PulledOrder,
  priceList: PricingPriceList,
  lines: PricedLine[],
): PricedOrder {
  const precision = currencyPrecisionOf(priceList);
  const total = (amounts: number[]) =>
    roundMoney(
      amounts.reduce((sum, amount) => sum + amount, 0),
      precision,
    );
  const chargeable = lines.filter((l) => !l.isFreeGood);
  const deliveries = pulled.snapshot.deliveries.map(({ sequence }) => {
    const cells = chargeable.flatMap((l) =>
      l.perDelivery.filter((c) => c.sequence === sequence),
    );
    return {
      sequence,
      quantity: cells.reduce((sum, c) => sum + c.quantity, 0),
      gross: total(cells.map((c) => c.gross)),
      net: total(cells.map((c) => c.net)),
    };
  });
  return {
    currency: priceList.currency,
    currencyPrecision: precision,
    priceListId: priceList.id,
    bundleVersion: null,
    asOf: pulled.snapshot.asOf,
    lines,
    deliveries,
    gross: total(chargeable.map((l) => l.gross)),
    net: total(chargeable.map((l) => l.net)),
    totalQuantity: chargeable.reduce((sum, l) => sum + l.quantity, 0),
    lineCount: chargeable.length,
    deliveryCount: deliveries.filter((d) => d.quantity > 0).length,
    violations: [],
    canSubmit: false,
    firings: [],
  };
}

/**
 * The order's schedule, with the `indskr_delivery…` columns copied from it: they name the schedule
 * on the device when the lookup was not saved, and keep the order's drop count.
 */
function pulledSchedule(
  order: Record<string, unknown>,
): OrderDraftSnapshot['schedule'] {
  const columns = Object.fromEntries(
    Object.entries(order)
      // Lowercase only: the client adds `<column>_Formatted` copies, which are not columns.
      .filter(([attribute]) => /^indskr_delivery[a-z0-9]+$/.test(attribute))
      .map(([attribute, value]) => [
        attribute,
        value instanceof Date ? isoOf(value) : value,
      ]),
  );
  const lookup = text(order._indskr_deliveryschedule_value);
  if (!lookup && !Object.keys(columns).length) return null;
  return {
    key: lookup,
    name: formattedText(order, '_indskr_deliveryschedule_value'),
    columns,
  };
}

function pulledDeliveries(
  rows: readonly Record<string, unknown>[],
  lines: readonly Record<string, unknown>[],
) {
  const used = new Set(
    lines.map((line) => text(line._indskr_orderdelivery_value)),
  );
  const deliveries = [...rows]
    .sort(
      (a, b) =>
        Number(a.indskr_sequence ?? 0) - Number(b.indskr_sequence ?? 0) ||
        isoOf(a.indskr_deliverydate).localeCompare(
          isoOf(b.indskr_deliverydate),
        ),
    )
    .map((row, index) => ({
      id: text(row.indskr_orderdeliveryid),
      sequence: index + 1,
      date: isoOf(row.indskr_deliverydate).slice(0, 10) || null,
      shipTo: text(row._indskr_ship_value) || null,
      isPrimary: row.indskr_isprimary === true,
      // Only a picked date keeps an empty drop besides the primary on the server (`buildSaveOrderRequest`).
      ...(row.indskr_isprimary !== true &&
      !used.has(text(row.indskr_orderdeliveryid))
        ? { datePicked: true }
        : {}),
    }));
  if (deliveries.length && !deliveries.some((d) => d.isPrimary))
    deliveries[0].isPrimary = true;
  return deliveries;
}

/** The drop an order without delivery rows is shown with. */
function soleDelivery(order: Record<string, unknown>) {
  return {
    id: crypto.randomUUID(),
    sequence: 1,
    date: localDayOf(isoOf(order.requestdeliveryby)),
    shipTo: null,
    isPrimary: true,
  };
}

/** The product on the order's first bought line, by name. */
function firstProductOf(lines: readonly Record<string, unknown>[]): string {
  const bought = lines.filter(
    (line) => line.indskr_isfreegood !== true && Number(line.quantity) > 0,
  );
  const position = (line: Record<string, unknown>) =>
    typeof line.lineitemnumber === 'number' ? line.lineitemnumber : Infinity;
  const first = [...bought].sort((a, b) => position(a) - position(b))[0];
  return first ? formattedText(first, '_productid_value') : '';
}

function pulledLines(
  rows: readonly Record<string, unknown>[],
  deliveries: readonly { id: string; sequence: number; isPrimary: boolean }[],
) {
  const sequenceOf = new Map(deliveries.map((d) => [d.id, d.sequence]));
  const primary = deliveries.find((d) => d.isPrimary)?.sequence ?? 1;
  const quantities = new Map<string, Record<number, number>>();
  const lineIds: OrderDraftSnapshot['lineIds'] = {};
  const lineNumbers: Record<string, number> = {};
  for (const line of rows) {
    const id = text(line.salesorderdetailid);
    const product = text(line._productid_value);
    if (!id || !product) continue;
    // A line saved before deliveries existed belongs to the primary drop.
    const sequence =
      sequenceOf.get(text(line._indskr_orderdelivery_value)) ?? primary;
    const reward = line.indskr_isfreegood === true;
    (lineIds[reward ? `${product}${REWARD_LINE_SUFFIX}` : product] ??= {})[
      sequence
    ] = id;
    if (typeof line.lineitemnumber === 'number')
      lineNumbers[id] = line.lineitemnumber;
    if (!reward) addQuantity(quantities, product, sequence, line.quantity);
  }
  return { quantities, lineIds, lineNumbers };
}

function addQuantity(
  quantities: Map<string, Record<number, number>>,
  product: string,
  sequence: number,
  raw: unknown,
): void {
  const quantity = Number(raw) || 0;
  if (quantity <= 0) return;
  const bySequence = quantities.get(product) ?? {};
  bySequence[sequence] = (bySequence[sequence] ?? 0) + quantity;
  quantities.set(product, bySequence);
}

/** The screen's header fields as the order holds them; a lookup keeps its table. */
function headerValues(
  order: Record<string, unknown>,
  attributes: readonly string[],
): AppUiFieldValues {
  const values: AppUiFieldValues = {};
  for (const attribute of attributes) {
    const lookup = `_${attribute}_value`;
    if (order[lookup] != null) {
      values[attribute] = {
        value: text(order[lookup]),
        label: formattedText(order, lookup),
        entity: text(order[`${lookup}${LOOKUP_TABLE}`]) || null,
      };
      continue;
    }
    const raw =
      order[attribute] instanceof Date
        ? isoOf(order[attribute])
        : order[attribute];
    if (raw === null || raw === undefined || typeof raw === 'object') continue;
    values[attribute] = {
      value: raw as string | number | boolean,
      label: formattedText(order, attribute) || String(raw),
    };
  }
  return values;
}

function placeholders(ids: string[]): Record<string, string> {
  return Object.fromEntries(ids.filter(Boolean).map((id) => [id, 'server']));
}
